/**
 * Vehicle catalogue loader (RAZ-327).
 *
 * The catalogue lives in sections/rb-vehicle-data.liquid and is fetched once through
 * the Section Rendering API at /?section_id=rb-vehicle-data. It is roughly 78 KB raw
 * and 11 KB over the wire, so it is cached in localStorage and shared by every
 * surface that needs it: the finder block, the header panel and the vehicle hub.
 *
 * Nothing here touches Alpine or the DOM, so the module can be unit tested in node
 * with a stubbed window and fetch.
 *
 * Failure is always survivable. A private window, blocked site data, a corrupt cache
 * entry or a 404 on the section each degrade to a plain per-page fetch or a clean
 * rejection, and the calling surface hides itself rather than showing an error.
 */

import { indexData } from './vehicleResolver.js';

/** localStorage key holding the cached catalogue document. */
export const STORAGE_KEY = 'rb_vehicle_data';

/** Section Rendering API path for the catalogue section. */
export const SECTION_PATH = '/?section_id=rb-vehicle-data';

/** Query parameter a content editor appends to see an edit immediately. */
export const REFRESH_PARAM = 'rb_vehicle_refresh';

/** Cache window used when the theme setting is missing or unusable. */
const DEFAULT_CACHE_MINUTES = 120;

const MINUTE_IN_MS = 60 * 1000;

/**
 * How long the catalogue request is given before it is abandoned.
 *
 * Long enough that a slow but working connection still lands, short enough that a
 * connection which will never answer gives the surface a definite "no" to degrade
 * against rather than an indefinite wait.
 */
export const FETCH_TIMEOUT_MS = 10000;

/** Pulls the catalogue document out of the rendered section markup. */
const SCRIPT_PATTERN = /<script[^>]+id="rb-vehicle-data"[^>]*>([\s\S]*?)<\/script>/i;

/**
 * The single in-flight or settled load for this page.
 *
 * Holding the promise rather than the value is what de-duplicates concurrent
 * callers: the header panel and a finder block asking in the same tick share one
 * request. It is cleared again on failure so a later caller can retry.
 */
let pendingLoad = null;

/**
 * The one debug warning this module emits, guarded so production consoles stay quiet.
 * @param {string} message
 * @param {*} detail
 */
function warnOnce(message, detail) {
  if (window.enableDebugMode) { console.warn(message, detail); }
}

/**
 * How long a cached catalogue stays usable, in milliseconds.
 * @returns {number}
 */
function cacheWindowMs() {
  let minutes = DEFAULT_CACHE_MINUTES;
  try {
    const configured = Number(window.themeSettings?.vehicleDataCacheMinutes);
    if (Number.isFinite(configured) && configured > 0) minutes = configured;
  } catch {
    // No themeSettings on this page; the default window is correct.
  }
  return minutes * MINUTE_IN_MS;
}

/**
 * Has the shopper (or a content editor) asked for a fresh catalogue?
 * @returns {boolean}
 */
function isRefreshRequested() {
  try {
    const href = window.location?.href ?? '';
    return new RegExp(`[?&]${REFRESH_PARAM}=1(?:[&#]|$)`).test(href);
  } catch {
    return false;
  }
}

/**
 * The cached catalogue document, or null when there is nothing usable to read.
 * Every failure mode — no storage, unparseable JSON, no timestamp, expired entry —
 * returns null so the caller simply fetches.
 *
 * @returns {Object|null}
 */
function readCachedDocument() {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const entry = JSON.parse(stored);
    if (!entry || typeof entry !== 'object' || !entry.doc) return null;

    const fetchedAt = Number(entry.fetched_at);
    if (!Number.isFinite(fetchedAt)) return null;

    const age = Date.now() - fetchedAt;
    if (age < 0 || age > cacheWindowMs()) return null;

    return entry.doc;
  } catch {
    return null;
  }
}

/**
 * Cache the catalogue document. A quota error or a blocked store is not worth
 * failing the load over, so it is swallowed and the next page fetches again.
 *
 * @param {Object} doc
 */
function writeCachedDocument(doc) {
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ fetched_at: Date.now(), doc })
    );
  } catch {
    // Private window, blocked site data or quota exceeded: cache-less is fine.
  }
}

/**
 * Fetch the catalogue section and parse its JSON document.
 *
 * The request is abandoned after FETCH_TIMEOUT_MS. Flaky mobile data and captive
 * portals both answer a request by never answering it, and an un-timed fetch leaves
 * the PDP fitment card on its grey skeleton for the rest of the page's life — exactly
 * where the "Fits your HiLux" answer belongs. A rejection is recoverable: loadVehicleData()
 * clears the shared promise, so the next surface to ask starts a fresh request.
 *
 * @param {boolean} isRefresh append the refresh parameter so an edge cache is skipped
 * @returns {Promise<Object>}
 */
async function fetchDocument(isRefresh) {
  const url = isRefresh ? `${SECTION_PATH}&${REFRESH_PARAM}=1` : SECTION_PATH;
  // Reached through globalThis so the module stays testable in node and degrades to an
  // un-timed fetch on the rare browser that has no AbortController rather than throwing.
  const Abort = globalThis.AbortController;
  const controller = typeof Abort === 'function' ? new Abort() : null;
  let hasTimedOut = false;
  // The clock covers the body as well as the headers: a connection that opens and
  // then stalls mid-response is the same dead card to a shopper as one that never
  // opens at all.
  const timer = controller
    ? setTimeout(() => {
        hasTimedOut = true;
        controller.abort();
      }, FETCH_TIMEOUT_MS)
    : null;

  try {
    const response = await fetch(url, {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
      ...(controller ? { signal: controller.signal } : {}),
    });

    if (!response.ok) {
      throw new Error(`rb-vehicle-data responded with HTTP ${response.status}`);
    }

    const markup = await response.text();
    const found = SCRIPT_PATTERN.exec(markup);
    if (!found) {
      throw new Error('rb-vehicle-data returned no catalogue script block');
    }

    return JSON.parse(found[1]);
  } catch (error) {
    if (hasTimedOut) {
      throw new Error(`rb-vehicle-data did not respond within ${FETCH_TIMEOUT_MS}ms`);
    }
    throw error;
  } finally {
    if (timer !== null) clearTimeout(timer);
  }
}

/**
 * Resolve the indexed vehicle catalogue.
 *
 * Reads the cache first, falls back to one fetch, and hands every caller on the page
 * the same promise so two surfaces asking at once make one request.
 *
 * @returns {Promise<Object>} index from indexData(), ready for the resolver
 */
export function loadVehicleData() {
  if (pendingLoad) return pendingLoad;

  const isRefresh = isRefreshRequested();
  const cached = isRefresh ? null : readCachedDocument();

  if (cached) {
    try {
      // indexData runs before the assignment, so a corrupt cache cannot poison the
      // shared promise; it falls through to a fetch instead.
      pendingLoad = Promise.resolve(indexData(cached));
      return pendingLoad;
    } catch {
      clearVehicleDataCache();
    }
  }

  pendingLoad = fetchDocument(isRefresh)
    .then((doc) => {
      const index = indexData(doc);
      writeCachedDocument(doc);
      return index;
    })
    .catch((error) => {
      pendingLoad = null;
      warnOnce('rb-vehicle-data could not be loaded; vehicle surfaces will stay hidden.', error);
      throw error;
    });

  return pendingLoad;
}

/**
 * Drop both the stored catalogue and this page's shared promise.
 * Used by the unit tests, and by the loader itself when a cache entry will not index.
 */
export function clearVehicleDataCache() {
  pendingLoad = null;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}
